import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, Tooltip } from 'recharts';
import Card from '../../components/ui/card';
import DashboardCardHeader from '../../components/ui/DashboardCardHeader';
import SubmissionActivityIcon from '../../components/ui/icons/SubmissionActivityIcon';
import { FiAlertCircle } from 'react-icons/fi';
import PropTypes from 'prop-types';
import SublyApi from '../../helpers/Api';
import {
	aggregateSubmissionsByDate,
	detectActivitySpikes,
	calculateActivityStats,
	getDateRangeForDays,
	validateSubmissionData
} from '../../utils/submissionActivityHelper';
import {
	SPIKE_DETECTION_DEFAULTS,
	API_DEFAULTS,
	UI_CONSTANTS,
	INTENSITY_THRESHOLDS,
	ERROR_MESSAGES
} from '../../utils/submissionActivityConstants';
import { handleApiError, logError } from '../../utils/errorHandler';
import { AdaptivePoller } from '../../utils/adaptivePolling';
import { useNotifications } from '../../contexts/NotificationContext';
import { notificationEvents } from '../../utils/notificationEvents';
import sseService from '../../utils/sseService';

// Custom hook for detecting mobile viewport
const useIsMobile = (breakpoint = 640) => {
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < breakpoint);
		};

		// Initial check
		checkMobile();

		// Listen for resize
		window.addEventListener('resize', checkMobile);
		return () => window.removeEventListener('resize', checkMobile);
	}, [breakpoint]);

	return isMobile;
};

const SubmissionActivityHeatmap = ({ daysRange = UI_CONSTANTS.defaultDaysRange, enableRealTime = true }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [activityData, setActivityData] = useState([]);
	const [lastRefresh, setLastRefresh] = useState(null);
	const lastSubmissionCountRef = useRef(0); // Use ref to avoid triggering useCallback dependencies
	const [poller, setPoller] = useState(null);
	const lastApiCallRef = useRef(0); // Track last API call timestamp for throttling
	const pendingApiCallRef = useRef(false); // Prevent multiple simultaneous API calls
	const fetchActivityDataRef = useRef(null); // Ref to avoid circular dependency

	// Detect mobile viewport for responsive visualization
	const isMobile = useIsMobile(640);

	// Get admin token from Redux store
	const adminToken = useSelector((state) => state.admin.token);

	// Get SSE connection status from notification context
	const { sseConnected } = useNotifications();
	
	// Throttled lightweight check for new submissions (counts only)
	const checkForNewSubmissions = useCallback(async () => {
		if (!adminToken || !enableRealTime) return;
		
		// Throttle API calls to prevent excessive requests
		const now = Date.now();
		const timeSinceLastCall = now - lastApiCallRef.current;
		const minimumInterval = 30000; // 30 seconds minimum between calls
		
		if (timeSinceLastCall < minimumInterval) {
			if (process.env.NODE_ENV === 'development') {
				console.log(`⏰ API call throttled, ${minimumInterval - timeSinceLastCall}ms remaining`);
			}
			return;
		}
		
		// Prevent multiple simultaneous calls
		if (pendingApiCallRef.current) {
			if (process.env.NODE_ENV === 'development') {
				console.log('🔄 API call already in progress, skipping');
			}
			return;
		}
		
		try {
			pendingApiCallRef.current = true;
			lastApiCallRef.current = now;
			
			const today = new Date().toISOString().split('T')[0];
			
			// Use optimized count-only endpoint
			const response = await SublyApi.getSubmissionCount(adminToken, {
				startDate: today,
				endDate: today
			});
			
			const currentCount = response?.count || 0;
			
			// Check if count has changed - auto-refresh if new submissions detected
			if (lastSubmissionCountRef.current > 0 && currentCount > lastSubmissionCountRef.current) {
				if (process.env.NODE_ENV === 'development') {
					console.log(`🔔 New submissions detected: ${currentCount} (was ${lastSubmissionCountRef.current}), auto-refreshing...`);
				}
				// Auto-refresh data when new submissions are detected (use ref to avoid circular dependency)
				if (fetchActivityDataRef.current) {
					fetchActivityDataRef.current(true);
				}
			}

			lastSubmissionCountRef.current = currentCount;
		} catch (err) {
			// Let adaptive poller handle error counting and backoff
			throw err;
		} finally {
			pendingApiCallRef.current = false;
		}
	}, [adminToken, enableRealTime]);
	
	// Throttled fetch submission activity data from API
	const fetchActivityData = useCallback(async (forceRefresh = false) => {
		if (!adminToken) {
			console.error('No admin token available for submission activity fetch');
			setError(ERROR_MESSAGES.authRequired);
			setIsLoading(false);
			return;
		}
		
		// Throttle full data fetches unless explicitly forced
		if (!forceRefresh) {
			const now = Date.now();
			const timeSinceLastCall = now - lastApiCallRef.current;
			const minimumInterval = 60000; // 60 seconds minimum for full data fetches
			
			if (timeSinceLastCall < minimumInterval) {
				if (process.env.NODE_ENV === 'development') {
					console.log(`⏰ Full data fetch throttled, ${minimumInterval - timeSinceLastCall}ms remaining`);
				}
				return;
			}
		}
		
		// Prevent multiple simultaneous calls
		if (pendingApiCallRef.current && !forceRefresh) {
			if (process.env.NODE_ENV === 'development') {
				console.log('🔄 Full data fetch already in progress, skipping');
			}
			return;
		}
		
		setIsLoading(true);
		setError(null);
		pendingApiCallRef.current = true;
		lastApiCallRef.current = Date.now();
		
		try {
			if (process.env.NODE_ENV === 'development') {
				console.log(`🔍 Fetching ${daysRange}-day submission activity data...`);
			}
			
			// Get date range for API call
			const { startDate, endDate } = getDateRangeForDays(daysRange);
			
			// Fetch submission data from API - only use cache busting when explicitly forced
			const response = await SublyApi.getAdminFormSubmissions(adminToken, {
				startDate,
				endDate,
				limit: API_DEFAULTS.submissionFetchLimit,
				forceRefresh
			});
			
			// Extract submissions from response
			const rawSubmissions = response?.submissions || response || [];
			
			// Validate and clean submission data
			const validSubmissions = validateSubmissionData(rawSubmissions);
			
			// Aggregate submissions by date
			const aggregatedData = aggregateSubmissionsByDate(validSubmissions, daysRange);
			
			// Apply spike detection
			const dataWithSpikes = detectActivitySpikes(aggregatedData, {
				spikeThresholdMultiplier: SPIKE_DETECTION_DEFAULTS.spikeThresholdMultiplier,
				minimumSpikeCount: SPIKE_DETECTION_DEFAULTS.minimumSpikeCount,
				rollingWindowDays: SPIKE_DETECTION_DEFAULTS.rollingWindowDays
			});
			
			if (process.env.NODE_ENV === 'development') {
				console.log(`📊 Processed ${validSubmissions.length}/${rawSubmissions.length} submissions`);
				console.log('📈 Activity data ready:', dataWithSpikes.length, 'days');
			}
			
			setActivityData(dataWithSpikes);
			setLastRefresh(new Date());
			
			// Set initial submission count for real-time detection
			const todayData = dataWithSpikes.find(day => {
				const today = new Date().toISOString().split('T')[0];
				const dayDate = day.date.toISOString().split('T')[0];
				return dayDate === today;
			});
			if (todayData && lastSubmissionCountRef.current === 0) {
				lastSubmissionCountRef.current = todayData.count;
			}
			
		} catch (err) {
			logError(err, 'fetchActivityData', { daysRange, adminToken: !!adminToken });
			setError(handleApiError(err, 'fetching submission activity'));
			
			// Set empty data on error
			setActivityData([]);
		} finally {
			setIsLoading(false);
			pendingApiCallRef.current = false;
		}
	}, [adminToken, daysRange]);

	// Keep the ref updated so checkForNewSubmissions can use it
	fetchActivityDataRef.current = fetchActivityData;

	// Fetch data on component mount and when dependencies change
	useEffect(() => {
		fetchActivityData();
	}, [fetchActivityData]);
	
	// Create stable event handlers that auto-refresh data
	const stableHandlers = useMemo(() => {
		const handleNewNotification = (data) => {
			console.log('📊 SubmissionActivityHeatmap: New notification received, auto-refreshing...');
			fetchActivityData(true);
		};

		const handleCountUpdate = (data) => {
			console.log('📊 SubmissionActivityHeatmap: Count update received, auto-refreshing...');
			fetchActivityData(true);
		};

		const handleRefreshCount = () => {
			console.log('📊 SubmissionActivityHeatmap: Refresh count event received, auto-refreshing...');
			fetchActivityData(true);
		};

		return { handleNewNotification, handleCountUpdate, handleRefreshCount };
	}, [fetchActivityData]);
	
	// Listen for real-time notification events
	useEffect(() => {
		if (!enableRealTime || !adminToken) return;
		
		const { handleNewNotification, handleCountUpdate, handleRefreshCount } = stableHandlers;
		
		// Add SSE event listeners
		if (sseConnected) {
			sseService.addEventListener('new_notification', handleNewNotification);
			sseService.addEventListener('count_update', handleCountUpdate);
		}
		
		// Add notification event listeners
		notificationEvents.addEventListener(notificationEvents.REFRESH_COUNT, handleRefreshCount);
		
		// Cleanup
		return () => {
			if (sseConnected) {
				sseService.removeEventListener('new_notification', handleNewNotification);
				sseService.removeEventListener('count_update', handleCountUpdate);
			}
			notificationEvents.removeEventListener(notificationEvents.REFRESH_COUNT, handleRefreshCount);
		};
	}, [adminToken, enableRealTime, sseConnected, stableHandlers]); // Fixed: use stable handlers
	
	// Create stable poller reference to prevent constant recreation
	const stableCheckFunction = useRef(checkForNewSubmissions);
	stableCheckFunction.current = checkForNewSubmissions;
	
	// Set up adaptive real-time polling for new submissions (only when SSE is not connected)
	useEffect(() => {
		if (!enableRealTime || !adminToken) {
			// Clean up existing poller if disabled
			if (poller) {
				poller.destroy();
				setPoller(null);
			}
			return;
		}
		
		// Only use adaptive polling when SSE is not connected
		if (sseConnected) {
			// SSE is connected, clean up poller if it exists
			if (poller) {
				console.log('🔄 SubmissionActivityHeatmap: SSE connected, stopping adaptive polling');
				poller.destroy();
				setPoller(null);
			}
			return;
		}
		
		// SSE is not connected, use adaptive polling as fallback
		console.log('🔄 SubmissionActivityHeatmap: SSE not connected, using adaptive polling');
		
		// Create adaptive poller with stable function reference
		const adaptivePoller = new AdaptivePoller(() => stableCheckFunction.current(), {
			baseInterval: UI_CONSTANTS.pollingInterval,
			minInterval: 30000, // 30 seconds minimum
			maxInterval: 3 * 60 * 1000, // 3 minutes maximum
			enableVisibilityOptimization: true,
			enableAdaptiveInterval: true,
			maxRetries: 3
		});
		
		setPoller(adaptivePoller);
		adaptivePoller.start();
		
		if (process.env.NODE_ENV === 'development') {
			console.log('🚀 Started adaptive polling for submission activity (SSE fallback)');
		}
		
		return () => {
			adaptivePoller.destroy();
		};
	}, [adminToken, enableRealTime, sseConnected]); // Fixed: stable dependencies only
	
	// Clean up poller when component unmounts
	useEffect(() => {
		return () => {
			if (poller) {
				poller.destroy();
			}
		};
	}, []);
	
	// Calculate statistics
	const stats = useMemo(() => calculateActivityStats(activityData), [activityData]);

	// Mobile-optimized data: show last 7 days for cleaner mobile view
	const mobileActivityData = useMemo(() => {
		if (activityData.length === 0) return [];
		return activityData.slice(-7).map(day => ({
			...day,
			shortLabel: day.dayLabel?.slice(0, 3) || day.dateLabel?.slice(-5) || ''
		}));
	}, [activityData]);

	// Calculate max count for scaling (with fallback for empty data)
	const maxCount = useMemo(() => {
		if (activityData.length === 0) return 1;
		return Math.max(...activityData.map(d => d.count), 1); // Ensure minimum of 1
	}, [activityData]);

	// Max count for mobile (last 7 days)
	const mobileMaxCount = useMemo(() => {
		if (mobileActivityData.length === 0) return 1;
		return Math.max(...mobileActivityData.map(d => d.count), 1);
	}, [mobileActivityData]);
	
	// Enhanced intensity level for heatmap with Jane Rothe colors
	const getIntensityClass = (count) => {
		if (maxCount <= 1) return 'bg-gray-100'; // Handle empty data case
		const percentage = (count / maxCount) * 100;
		if (percentage === 0) return 'bg-gray-100';
		if (percentage < INTENSITY_THRESHOLDS.veryLow) return 'bg-blue-100';
		if (percentage < INTENSITY_THRESHOLDS.low) return 'bg-blue-200';
		if (percentage < INTENSITY_THRESHOLDS.medium) return 'bg-blue-300';
		if (percentage < INTENSITY_THRESHOLDS.high) return 'bg-blue-400';
		return 'bg-primary'; // Jane Rothe pink for peak activity
	};
	
	// Retry handler for error state
	const handleRetry = () => {
		fetchActivityData(true); // Force refresh to bypass cache
	};
	
	if (isLoading && activityData.length === 0) {
		return (
			<Card className="overflow-hidden animate-pulse">
				<div className="p-4 sm:p-6">
					<div className="flex justify-between items-start mb-4">
						<div>
							<div className="h-4 bg-gray-200 rounded w-40 mb-2"></div>
							<div className="h-8 bg-gray-200 rounded w-24"></div>
						</div>
						<div className="rounded-full p-3 bg-gray-200">
							<div className="h-6 w-6 bg-gray-300 rounded"></div>
						</div>
					</div>
					<div className="h-32 bg-gray-200 rounded"></div>
				</div>
			</Card>
		);
	}
	
	return (
		<Card className="overflow-hidden bg-white border border-gray-100 h-full" role="region" aria-label="Submission Activity Dashboard">
			<div className="p-4 sm:p-6">
				{/* Error State */}
				{error && (
					<div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
						<div className="flex items-center">
							<FiAlertCircle className="h-4 w-4 text-red-500 mr-2" />
							<span className="text-sm text-red-700">{error}</span>
							<button
								onClick={handleRetry}
								className="ml-auto text-red-600 hover:text-red-700 text-sm underline"
							>
								Retry
							</button>
						</div>
					</div>
				)}
				
				{/* Header Section */}
				<DashboardCardHeader
					title="Submission Activity"
					value={`${stats.todayCount} today`}
					icon={
						<SubmissionActivityIcon
							className="h-7 w-7 text-clientBlack"
							activityLevel={stats.todayCount > stats.avgDaily * 1.5 ? "spike" : "normal"}
							showPinkAccent={true}
						/>
					}
				/>
				
				{/* Change Indicator */}
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center space-x-2">
						<span className="text-sm font-montserrat-regular text-gray-600">
							{stats.percentChange !== null ? (
								<>
									<span className={`font-montserrat-medium ${stats.percentChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
										{stats.useAbsoluteChange
											? `+${stats.percentChange}`
											: `${stats.percentChange >= 0 ? '+' : ''}${Math.abs(stats.percentChange).toFixed(0)}%`
										}
									</span>
									{' '}vs yesterday
								</>
							) : (
								<span className="font-montserrat-medium text-gray-500">N/A vs yesterday</span>
							)}
						</span>
						{lastRefresh && (
							<span className="text-xs text-gray-400">
								• Updated {lastRefresh.toLocaleTimeString()}
							</span>
						)}
					</div>
					{stats.spikeCount > 0 && (
						<div className="flex items-center space-x-1">
							<FiAlertCircle className="h-3 w-3 text-orange-500" />
							<span className="text-xs font-montserrat-medium text-orange-600">
								{stats.spikeCount} spike{stats.spikeCount > 1 ? 's' : ''} detected
							</span>
						</div>
					)}
				</div>
				
				{/* Activity Visualization - Mobile vs Desktop */}
				<div className="mb-4" role="img" aria-label={`${isMobile ? '7' : daysRange}-day submission activity chart showing ${stats.todayCount} submissions today with trend data`}>
					{activityData.length === 0 ? (
						<div className="text-center py-8 text-gray-500">
							<div className="text-sm">{ERROR_MESSAGES.noData}</div>
							{error && (
								<button
									onClick={handleRetry}
									className="mt-2 text-blue-600 hover:text-blue-700 text-sm underline"
								>
									Try refreshing
								</button>
							)}
						</div>
					) : isMobile ? (
						/* MOBILE: Simple 7-day bar chart with Recharts */
						<div className="h-32 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden p-2">
							<div className="text-xs font-montserrat-medium text-gray-500 mb-1">Last 7 days</div>
							<ResponsiveContainer width="100%" height="85%">
								<BarChart data={mobileActivityData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
									<defs>
										<linearGradient id="mobileBarGradient" x1="0" y1="0" x2="0" y2="1">
											<stop offset="5%" stopColor="#CC1DB9" stopOpacity={0.9}/>
											<stop offset="95%" stopColor="#CC1DB9" stopOpacity={0.4}/>
										</linearGradient>
									</defs>
									<XAxis
										dataKey="shortLabel"
										tick={{ fontSize: 10, fill: '#6b7280' }}
										tickLine={false}
										axisLine={false}
									/>
									<Tooltip
										contentStyle={{
											backgroundColor: '#1f2937',
											border: 'none',
											borderRadius: '6px',
											fontSize: '12px'
										}}
										labelStyle={{ color: '#9ca3af' }}
										itemStyle={{ color: '#fff' }}
										formatter={(value) => [`${value} submissions`, '']}
										labelFormatter={(label) => label}
									/>
									<Bar
										dataKey="count"
										fill="url(#mobileBarGradient)"
										radius={[4, 4, 0, 0]}
									/>
								</BarChart>
							</ResponsiveContainer>
						</div>
					) : (
						/* DESKTOP: Full 30-day heatmap view */
						<>
							<div className="flex items-end space-x-1">
								{activityData.map((day, index) => (
									<div key={index} className="flex-1 group relative">
										{/* Spike indicator */}
										{day.isSpike && (
											<div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
												<div className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse"></div>
											</div>
										)}

										{/* Activity bar */}
										<div
											className={`w-full rounded-t transition-all duration-200 group-hover:opacity-80 ${getIntensityClass(day.count)}`}
											style={{
												height: `${Math.max(UI_CONSTANTS.heatmapBarMinHeight, (day.count / maxCount) * UI_CONSTANTS.heatmapBarMaxHeight)}px`
											}}
											role="img"
											aria-label={`${day.count} submissions on ${day.dateLabel}`}
										/>

										{/* Tooltip on hover */}
										<div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
											<div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
												<div className="font-montserrat-medium">{day.count} submissions</div>
												<div className="font-montserrat-regular text-gray-300">{day.dateLabel}</div>
											</div>
											<div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
												<div className="border-4 border-transparent border-t-gray-900"></div>
											</div>
										</div>
									</div>
								))}
							</div>

							{/* Day labels */}
							<div className="flex items-center space-x-1 mt-1">
								{activityData.map((day, index) => (
									<div key={index} className="flex-1 text-center">
										<span className="text-xs font-montserrat-regular text-gray-400">
											{index === 0 || index === activityData.length - 1 ? day.dayLabel[0] : ''}
										</span>
									</div>
								))}
							</div>
						</>
					)}
				</div>

				{/* Enhanced Sparkline with Recharts - Desktop only */}
				{activityData.length > 0 && !isMobile && (
					<div className="h-16 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
						<ResponsiveContainer width="100%" height="100%">
							<AreaChart
								data={activityData}
								margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
							>
								<defs>
									<linearGradient id="submissionGradient" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
										<stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
									</linearGradient>
									<linearGradient id="spikeGradient" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor="#CC1DB9" stopOpacity={0.8}/>
										<stop offset="95%" stopColor="#CC1DB9" stopOpacity={0.1}/>
									</linearGradient>
								</defs>
								<Area
									type="monotone"
									dataKey="count"
									stroke="#3B82F6"
									strokeWidth={2}
									fill="url(#submissionGradient)"
									dot={false}
									activeDot={false}
								/>
							</AreaChart>
						</ResponsiveContainer>
					</div>
				)}
				
				{/* Footer Stats */}
				<div className="mt-3 pt-3 border-t border-gray-100">
					<div className="grid grid-cols-3 gap-4 text-xs">
						<div>
							<span className="font-montserrat-regular text-gray-500 block">{daysRange}-day total</span>
							<span className="font-montserrat-medium text-gray-900">
								{stats.totalSubmissions}
							</span>
						</div>
						<div>
							<span className="font-montserrat-regular text-gray-500 block">Daily avg</span>
							<span className="font-montserrat-medium text-gray-900">
								{stats.avgDaily}
							</span>
						</div>
						<div>
							<span className="font-montserrat-regular text-gray-500 block">Peak day</span>
							<span className="font-montserrat-medium text-gray-900">
								{stats.maxDaily}
							</span>
						</div>
					</div>
				</div>
			</div>
		</Card>
	);
};

SubmissionActivityHeatmap.propTypes = {
	daysRange: PropTypes.number,
	enableRealTime: PropTypes.bool
};

export default SubmissionActivityHeatmap;